import { invoke as tauriInvoke } from "@tauri-apps/api/core";
import {
  activeCloudProjectId,
  cloudEditorApiOrigin,
  isCloudAppHosted,
} from "./cloudEditorHost";
import { authorizedFetch } from "./cloudAuth";
import { demoInvoke, isDemoSessionActive } from "./demo/session";
import { isJust } from "./maybe";
import { isTauri } from "./runtime";

/**
 * Single entry point for calling a backend command, regardless of platform.
 *
 * Demo: in-memory fixture (`demo://…`). Desktop: Tauri IPC. Web: `POST
 * /invoke/{command}` — `zw-online` mirrors `src-tauri`'s command surface by
 * name, so the same command name and args shape works both places. Callers
 * never branch on platform themselves; this is the only place that does.
 *
 * Trust: successful invoke payloads are already deserialized by Rust serde
 * (Tauri / zw-online). Corrupt `.zwconfig` / `.zwlocal` / `.zwgraph` are
 * healed on read (defaults written back) before a payload is returned. Do
 * not re-validate those blobs with Zod here. Zod is for zw-cloud `/v1` JSON
 * DTOs only. Demo-only JSON strings use `parseZwConfigJson`.
 */
export async function invoke<T>(
  command: string,
  args?: Record<string, unknown>,
): Promise<T> {
  if (isDemoSessionActive()) {
    return demoInvoke(command, args) as Promise<T>;
  }

  if (isTauri()) {
    return tauriInvoke<T>(command, args);
  }

  if (isCloudAppHosted()) {
    const apiOrigin = cloudEditorApiOrigin();
    const headers: Record<string, string> = {
      "content-type": "application/json",
    };
    const projectId = activeCloudProjectId();
    if (isJust(projectId) && projectId.value.length > 0) {
      headers["X-Zw-Project-Id"] = projectId.value;
    }

    const res = await fetch(`${apiOrigin}/invoke/${command}`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(args ?? {}),
    });
    if (res.ok === false) {
      const body = await res.json().catch(() => ({}));
      const errMsg =
        typeof body.error === "string" && body.error !== ""
          ? body.error
          : `${command} failed (${res.status})`;
      throw new Error(errMsg);
    }
    return res.json();
  }

  const res = await authorizedFetch(`/invoke/${command}`, {
    method: "POST",
    body: JSON.stringify(args ?? {}),
  });
  if (res.ok === false) {
    const body = await res.json().catch(() => ({}));
    const errMsg =
      typeof body.error === "string" && body.error !== ""
        ? body.error
        : `${command} failed (${res.status})`;
    throw new Error(errMsg);
  }
  return res.json();
}

/**
 * For commands with no web equivalent — native dialogs, window/menu
 * management, device licensing. Fails immediately and clearly instead of a
 * network round trip to `zw-online` for a command that will never exist
 * there (which would otherwise look identical to a command that's simply
 * not ported yet).
 *
 * This is a safety net, not the primary defense — the calling UI should
 * still gate the action behind `isTauri()` so the option never appears on
 * web in the first place.
 */
export async function tauriOnlyInvoke<T>(
  command: string,
  args?: Record<string, unknown>,
): Promise<T> {
  if (isTauri() === false) {
    throw new Error(`${command} is only available in the desktop app`);
  }
  return tauriInvoke<T>(command, args);
}
